import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { alunoService } from '@/services/api';
import {
  UserGroupIcon,
  ArrowLeftIcon,
  PencilIcon,
  MapPinIcon,
  AcademicCapIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  IdentificationIcon,
  HomeIcon
} from '@heroicons/react/24/outline';

export const AlunoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data: aluno, isLoading, error } = useQuery({
    queryKey: ['aluno', id],
    queryFn: () => alunoService.getById(Number(id)),
    enabled: !!id,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCPF = (cpf?: string) => {
    if (!cpf) return 'Não informado';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatRG = (rg?: string) => {
    if (!rg) return 'Não informado';
    return rg;
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar dados do aluno</p>
        <Button asChild className="mt-4">
          <Link to="/alunos">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Voltar para Lista
          </Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Carregando dados do aluno...</p>
      </div>
    );
  }

  if (!aluno) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Aluno não encontrado</p>
        <Button asChild className="mt-4">
          <Link to="/alunos">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Voltar para Lista
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Button variant="outline" asChild>
            <Link to="/alunos">
              <ArrowLeftIcon className="h-4 w-4" />

            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{aluno.nome}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Detalhes completos do aluno
            </p>
          </div>
        </div>
        <Button asChild>
          <Link to={`/alunos/${aluno.id}/editar`}>
            <PencilIcon className="h-4 w-4 mr-2" />
            Editar Aluno
          </Link>
        </Button>
      </div>

      {/* Informações Pessoais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2 text-blue-600" />
            Informações Pessoais
          </CardTitle>
          <CardDescription>
            Dados básicos e de identificação do aluno
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <IdentificationIcon className="h-4 w-4 mr-2" />
                ID
              </div>
              <p className="font-medium text-gray-900">{aluno.id}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <IdentificationIcon className="h-4 w-4 mr-2" />
                CPF
              </div>
              <p className="font-medium text-gray-900">{formatCPF(aluno.cpf)}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <IdentificationIcon className="h-4 w-4 mr-2" />
                RG
              </div>
              <p className="font-medium text-gray-900">{formatRG(aluno.rg)}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Data de Nascimento
              </div>
              <p className="font-medium text-gray-900">{formatDate(aluno.dataNascimento)}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                Email
              </div>
              <p className="font-medium text-gray-900">{aluno.email || 'Não informado'}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <PhoneIcon className="h-4 w-4 mr-2" />
                Telefone
              </div>
              <p className="font-medium text-gray-900">{aluno.telefone || 'Não informado'}</p>
            </div>
          </div>

          {aluno.endereco && (
            <div className="mt-6 space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <HomeIcon className="h-4 w-4 mr-2" />
                Endereço
              </div>
              <p className="font-medium text-gray-900">{aluno.endereco}</p>
            </div>
          )}

          {aluno.telefonePai && (
            <div className="mt-6 space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <PhoneIcon className="h-4 w-4 mr-2" />
                Telefone do Pai/Responsável
              </div>
              <p className="font-medium text-gray-900">{aluno.telefonePai}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informações Acadêmicas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AcademicCapIcon className="h-5 w-5 mr-2 text-green-600" />
            Informações Acadêmicas
          </CardTitle>
          <CardDescription>
            Dados relacionados ao curso e série do aluno
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <AcademicCapIcon className="h-4 w-4 mr-2" />
                Curso
              </div>
              <p className="font-medium text-gray-900">
                {aluno.curso?.nome || 'Não informado'}
              </p>
              {aluno.curso?.nivel && (
                <p className="text-sm text-gray-600">Nível: {aluno.curso.nivel}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <AcademicCapIcon className="h-4 w-4 mr-2" />
                Série
              </div>
              <p className="font-medium text-gray-900">{aluno.serie || 'Não informado'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informações de Localização */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MapPinIcon className="h-5 w-5 mr-2 text-purple-600" />
            Localização
          </CardTitle>
          <CardDescription>
            Informações sobre a cidade do aluno
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center text-sm text-gray-500">
              <MapPinIcon className="h-4 w-4 mr-2" />
              Cidade
            </div>
            <p className="font-medium text-gray-900">
              {aluno.cidade?.nome || 'Não informado'} - {aluno.cidade?.uf || ''}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
